@if (hasWorkflow) {
  <div class="workflow-editor-tree">
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-left">
        <span class="toolbar-title">
          <i class="fa fa-project-diagram"></i>
          Workflow Designer
        </span>
        <button class="toolbar-btn primary" (click)="addState(null)" [disabled]="readonly" title="Add State">
          <i class="fa fa-plus"></i>
          <span>Add State</span>
        </button>
      </div>
      <div class="toolbar-right">
        <span class="hint">
          Click <strong>+</strong> under a state to branch • Drag connector to link • Scroll to zoom
        </span>
        <button class="toolbar-btn" (click)="resetView()" title="Fit to View">
          <i class="fa fa-expand"></i>
        </button>
      </div>
    </div>
    <div class="editor-body">
      <!-- Canvas -->
      <div class="canvas-container"
        #canvasContainer
        (mousedown)="onCanvasMouseDown($event)"
        (wheel)="onCanvasWheel($event)"
        (click)="clearSelection()">
        <svg #svgElement
          [attr.viewBox]="viewBox.x + ' ' + viewBox.y + ' ' + viewBox.width + ' ' + viewBox.height"
          class="workflow-canvas">
          <!-- Definitions -->
          <defs>
            <marker id="arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
            </marker>
            <marker id="arrow-selected" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#3b82f6" />
            </marker>
            <marker id="arrow-back" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#f59e0b" />
            </marker>
            <marker id="arrow-drag" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#8b5cf6" />
            </marker>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="4" flood-opacity="0.1"/>
            </filter>
            <filter id="shadow-hover" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="4" stdDeviation="6" flood-opacity="0.15"/>
            </filter>
            <filter id="shadow-drag" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="6" stdDeviation="10" flood-opacity="0.2"/>
            </filter>
            <!-- Subtle grid -->
            <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
              <circle cx="15" cy="15" r="0.5" fill="#cbd5e1"/>
            </pattern>
          </defs>
          <!-- Background grid -->
          <rect width="100%" height="100%" fill="url(#grid)" opacity="0.5"/>
          <!-- START label -->
          @if (layout && layout.nodes.length > 0) {
            <g class="start-label">
              <text [attr.x]="layout.width / 2"
                [attr.y]="CANVAS_PADDING + 25"
                text-anchor="middle"
                class="start-text">
                START
              </text>
              <!-- Arrow to first state(s) -->
              @for (node of layout.nodes; track node) {
                <g>
                  @if (node.state.IsInitial) {
                    <line
                      [attr.x1]="layout.width / 2"
                      [attr.y1]="CANVAS_PADDING + 40"
                      [attr.x2]="node.x + node.width / 2"
                      [attr.y2]="node.y - 5"
                      class="start-arrow"
                      marker-end="url(#arrow)" />
                  }
                </g>
              }
            </g>
          }
          <!-- Drop zones (visible when dragging or on hover) -->
          @if (layout && !readonly) {
            <g class="drop-zones"
              [class.dragging-active]="isDragging">
              @for (zone of layout.dropZones; track zone) {
                <g
                  class="drop-zone-group"
                  [class.active]="isDropZoneActive(zone)"
                  [class.disabled]="zone.isDisabled"
                  [attr.title]="zone.label"
                  (click)="addStateAtDropZone(zone, $event)">
                  <rect class="drop-zone"
                    [attr.x]="zone.x"
                    [attr.y]="zone.y"
                    [attr.width]="zone.width"
                    [attr.height]="zone.height"
                    rx="6" />
                    <text class="drop-zone-icon"
                      [attr.x]="zone.x + zone.width / 2"
                      [attr.y]="zone.y + zone.height / 2"
                      text-anchor="middle"
                      dominant-baseline="central">
                      {{ zone.isDisabled ? '×' : '+' }}
                    </text>
                  </g>
                }
              </g>
            }
            <!-- Transitions -->
            @if (layout) {
              <g class="transitions">
                @for (lt of layout.transitions; track lt) {
                  <g
                    class="transition-group"
                    [class.selected]="isTransitionSelected(lt.transition)"
                    [class.backward]="lt.isBackward"
                    (click)="selectTransition(lt.transition, $event)">
                    <path [attr.d]="lt.path"
                      class="transition-path"
                      [attr.marker-end]="lt.isBackward ? 'url(#arrow-back)' : (isTransitionSelected(lt.transition) ? 'url(#arrow-selected)' : 'url(#arrow)')" />
                      <path [attr.d]="lt.path" class="transition-hitarea" />
                      <g [attr.transform]="'translate(' + lt.labelX + ',' + lt.labelY + ')'"
                        class="transition-label-group">
                        <rect class="transition-label-bg"
                          x="-50" y="-12"
                          width="100" height="24" rx="4" />
                          <text class="transition-label"
                            text-anchor="middle"
                            dominant-baseline="middle">
                            {{ truncateLabel(lt.transition.Name) }}
                          </text>
                        </g>
                      </g>
                    }
                  </g>
                }
                <!-- Connection drag line -->
                @if (isConnecting) {
                  <g class="connection-drag">
                    <line [attr.x1]="connectionLine.x1"
                      [attr.y1]="connectionLine.y1"
                      [attr.x2]="connectionLine.x2"
                      [attr.y2]="connectionLine.y2"
                      class="connection-line"
                      marker-end="url(#arrow-drag)" />
                    </g>
                  }
                  <!-- State nodes -->
                  @if (layout) {
                    <g class="states">
                      @for (node of layout.nodes; track node) {
                        <g
                          [class]="getStateClass(node.state)"
                          [class.dragging]="isDraggingNode(node)"
                          [class.connection-target]="isConnectionTarget(node)"
                          [attr.transform]="getNodeTransform(node)"
                          [attr.filter]="isDraggingNode(node) ? 'url(#shadow-drag)' : 'url(#shadow)'"
                          (mousedown)="startDrag(node, $event)"
                          (click)="selectState(node.state, $event)">
                          <!-- State background -->
                          <rect class="state-bg"
                            [attr.width]="node.width"
                            [attr.height]="node.height"
                            rx="12" />
                            <!-- State type indicator -->
                            <rect class="state-indicator"
                              x="0" y="0"
                              width="6"
                              [attr.height]="node.height"
                              rx="3" />
                              <!-- State name -->
                              <text class="state-name"
                                [attr.x]="node.width / 2"
                                [attr.y]="node.height / 2 - 6"
                                text-anchor="middle"
                                dominant-baseline="middle">
                                {{ node.state.Name }}
                              </text>
                              <!-- State type badge -->
                              <text class="state-type"
                                [attr.x]="node.width / 2"
                                [attr.y]="node.height / 2 + 14"
                                text-anchor="middle"
                                dominant-baseline="middle">
                                {{ node.state.IsInitial ? 'Initial' : (node.state.IsFinal ? 'Terminal' : 'Active') }}
                              </text>
                              <!-- Connection handle -->
                              @if (!readonly && !isDragging) {
                                <g class="connection-handle"
                                  [attr.transform]="'translate(' + (node.width / 2) + ',' + node.height + ')'"
                                  (mousedown)="startConnection(node, $event)">
                                  <circle r="12" class="handle-bg" />
                                  <circle r="5" class="handle-inner" />
                                </g>
                              }
                            </g>
                          }
                        </g>
                      }
                    </svg>
                  </div>
                  <!-- Properties Panel -->
                  <div class="properties-panel" [class.has-selection]="selection.type !== null">
                    <!-- No selection -->
                    @if (selection.type === null) {
                      <div class="panel-header">
                        <h3>Workflow</h3>
                      </div>
                      <div class="panel-body">
                        <div class="field">
                          <label>Name</label>
                          <input class="input"
                            [(ngModel)]="workflowCopy!.Name"
                            [readonly]="readonly"
                            (blur)="onPropertyChange()" />
                          </div>
                          <div class="field">
                            <label>Description</label>
                            <textarea class="textarea"
                              rows="3"
                              [(ngModel)]="workflowCopy!.Description"
                              [readonly]="readonly"
                            (blur)="onPropertyChange()"></textarea>
                          </div>
                          <div class="stats">
                            <div class="stat">
                              <span class="stat-value">{{ workflowCopy!.States.length }}</span>
                              <span class="stat-label">States</span>
                            </div>
                            <div class="stat">
                              <span class="stat-value">{{ workflowCopy!.Transitions.length }}</span>
                              <span class="stat-label">Transitions</span>
                            </div>
                          </div>
                          <div class="help-section">
                            <h4>Quick Guide</h4>
                            <ul>
                              <li><strong>Add state:</strong> Click <span class="kbd">+</span> button or the zone below a state</li>
                              <li><strong>Connect:</strong> Drag from the circle at the bottom of a state</li>
                              <li><strong>Branch:</strong> Connect one state to multiple children</li>
                              <li><strong>Loop:</strong> Connect to any earlier state</li>
                              <li><strong>Edit:</strong> Click on a state or transition</li>
                            </ul>
                          </div>
                        </div>
                      }
                      <!-- State selected -->
                      @if (selectedState) {
                        <div class="panel-header">
                          <h3>State</h3>
                          <button class="close-btn" (click)="clearSelection()">
                            <i class="fa fa-times"></i>
                          </button>
                        </div>
                        <div class="panel-body">
                          <div class="field">
                            <label>Name</label>
                            <input class="input"
                              [(ngModel)]="selectedState.Name"
                              [readonly]="readonly"
                              (blur)="onPropertyChange()" />
                            </div>
                            <div class="field">
                              <label>Key</label>
                              <input class="input"
                                [(ngModel)]="selectedState.StateKey"
                                [readonly]="readonly"
                                (blur)="onPropertyChange()" />
                              </div>
                              <div class="field">
                                <label>Description</label>
                                <textarea class="textarea"
                                  rows="3"
                                  [(ngModel)]="selectedState.Description"
                                  [readonly]="readonly"
                                (blur)="onPropertyChange()"></textarea>
                              </div>
                              <div class="field">
                                <label>State Type</label>
                                @if (!readonly) {
                                  <div class="type-buttons">
                                    <button class="type-btn"
                                      [class.active]="selectedState.IsInitial"
                                      [class.initial]="selectedState.IsInitial"
                                      (click)="setStateAsInitial()">
                                      <i class="fa fa-play"></i> Initial
                                    </button>
                                    <button class="type-btn"
                                      [class.active]="!selectedState.IsInitial && !selectedState.IsFinal"
                                      [class.active-type]="!selectedState.IsInitial && !selectedState.IsFinal"
                                      (click)="setStateAsActive()">
                                      <i class="fa fa-circle"></i> Active
                                    </button>
                                    <button class="type-btn"
                                      [class.active]="selectedState.IsFinal"
                                      [class.terminal]="selectedState.IsFinal"
                                      (click)="setStateAsFinal()">
                                      <i class="fa fa-stop"></i> Terminal
                                    </button>
                                  </div>
                                }
                              </div>
                              @if (!readonly) {
                                <div class="panel-actions">
                                  <button class="delete-btn" (click)="removeSelectedState()">
                                    <i class="fa fa-trash"></i> Delete State
                                  </button>
                                </div>
                              }
                            </div>
                          }
                          <!-- Transition selected -->
                          @if (selectedTransition) {
                            <div class="panel-header">
                              <h3>Transition</h3>
                              <button class="close-btn" (click)="clearSelection()">
                                <i class="fa fa-times"></i>
                              </button>
                            </div>
                            <div class="panel-body">
                              <div class="field">
                                <label>Name</label>
                                <input class="input"
                                  [(ngModel)]="selectedTransition.Name"
                                  [readonly]="readonly"
                                  (blur)="onPropertyChange()" />
                                </div>
                                <div class="field">
                                  <label>From</label>
                                  <select class="input"
                                    [(ngModel)]="selectedTransition.FromStateKey"
                                    [disabled]="readonly"
                                    (change)="onPropertyChange()">
                                    @for (state of workflowCopy!.States; track state) {
                                      <option [value]="state.StateKey">
                                        {{ state.Name }}
                                      </option>
                                    }
                                  </select>
                                </div>
                                <div class="field">
                                  <label>To</label>
                                  <select class="input"
                                    [(ngModel)]="selectedTransition.ToStateKey"
                                    [disabled]="readonly"
                                    (change)="onPropertyChange()">
                                    @for (state of workflowCopy!.States; track state) {
                                      <option [value]="state.StateKey">
                                        {{ state.Name }}
                                      </option>
                                    }
                                  </select>
                                </div>
                                <div class="field">
                                  <label>Description</label>
                                  <textarea class="textarea"
                                    rows="2"
                                    [(ngModel)]="selectedTransition.Description"
                                    [readonly]="readonly"
                                  (blur)="onPropertyChange()"></textarea>
                                </div>
                                <div class="field">
                                  <label>Trigger</label>
                                  <div class="checkbox-group">
                                    <label class="checkbox">
                                      <input type="checkbox"
                                        [(ngModel)]="selectedTransition.IsManualTrigger"
                                        [disabled]="readonly"
                                        (change)="onPropertyChange()" />
                                        <span>Manual</span>
                                      </label>
                                      <label class="checkbox">
                                        <input type="checkbox"
                                          [(ngModel)]="selectedTransition.IsAutomaticTrigger"
                                          [disabled]="readonly"
                                          (change)="onPropertyChange()" />
                                          <span>Automatic</span>
                                        </label>
                                      </div>
                                    </div>
                                    <div class="field">
                                      <label>Priority</label>
                                      <input class="input"
                                        type="number"
                                        [(ngModel)]="selectedTransition.Priority"
                                        [readonly]="readonly"
                                        (blur)="onPropertyChange()" />
                                      </div>
                                      <!-- Conditions Section (Expression Builder) - Only for automatic transitions -->
                                      @if (selectedTransition.IsAutomaticTrigger) {
                                        <div class="conditions-section">
                                          <div class="section-header">
                                            <label>Conditions</label>
                                            @if (!readonly) {
                                              <button class="add-condition-btn"
                                                (click)="addCondition(); $event.stopPropagation()"
                                                title="Add Condition">
                                                <i class="fa fa-plus"></i>
                                              </button>
                                            }
                                          </div>
                                          @if (!selectedTransition.Conditions?.length && !readonly) {
                                            <div class="conditions-help">
                                              <i class="fa fa-info-circle"></i>
                                              <span>No conditions. This transition will trigger automatically.</span>
                                            </div>
                                          }
                                          @if (!selectedTransition.Conditions?.length && readonly) {
                                            <div class="conditions-help">
                                              <i class="fa fa-bolt"></i>
                                              <span>Always triggers automatically (no conditions)</span>
                                            </div>
                                          }
                                          <!-- Readonly view - show conditions as readable text -->
                                          @if (selectedTransition.Conditions?.length && readonly) {
                                            <div class="condition-list readonly-conditions">
                                              @for (condition of selectedTransition.Conditions; track condition; let i = $index) {
                                                <div class="condition-item readonly">
                                                  <div class="condition-display">
                                                    <span class="condition-property">{{ getPropertyLabel(condition.PropertyName) }}</span>
                                                    <span class="condition-operator">{{ getOperatorLabel(condition.Operator) }}</span>
                                                    <span class="condition-value">{{ condition.Value }}</span>
                                                  </div>
                                                </div>
                                              }
                                            </div>
                                          }
                                          <!-- Editable view - show form controls -->
                                          @if (selectedTransition.Conditions?.length && !readonly) {
                                            <div class="condition-list" (click)="$event.stopPropagation()">
                                              @for (condition of selectedTransition.Conditions; track condition; let i = $index) {
                                                <div class="condition-item">
                                                  <div class="condition-row">
                                                    <select class="condition-field"
                                                      [(ngModel)]="condition.PropertyName"
                                                      (change)="onConditionChange()"
                                                      (click)="$event.stopPropagation()">
                                                      <option value="">Select property...</option>
                                                      @for (prop of conditionProperties; track prop) {
                                                        <option [value]="prop.value">
                                                          {{ prop.label }}
                                                        </option>
                                                      }
                                                    </select>
                                                    <select class="condition-operator"
                                                      [(ngModel)]="condition.Operator"
                                                      (change)="onConditionChange()"
                                                      (click)="$event.stopPropagation()">
                                                      @for (op of conditionOperators; track op) {
                                                        <option [value]="op.value">
                                                          {{ op.label }}
                                                        </option>
                                                      }
                                                    </select>
                                                    @switch (getPropertyType(condition.PropertyName)) {
                                                      @case ('boolean') {
                                                        <select
                                                          class="condition-value"
                                                          [(ngModel)]="condition.Value"
                                                          (change)="onConditionChange()"
                                                          (click)="$event.stopPropagation()">
                                                          <option value="true">True</option>
                                                          <option value="false">False</option>
                                                        </select>
                                                      }
                                                      @case ('number') {
                                                        <input
                                                          class="condition-value"
                                                          type="number"
                                                          [(ngModel)]="condition.Value"
                                                          (blur)="onConditionChange()"
                                                          (click)="$event.stopPropagation()"
                                                          placeholder="Value" />
                                                      }
                                                      @default {
                                                        <input
                                                          class="condition-value"
                                                          type="text"
                                                          [(ngModel)]="condition.Value"
                                                          (blur)="onConditionChange()"
                                                          (click)="$event.stopPropagation()"
                                                          placeholder="Value" />
                                                      }
                                                    }
                                                    <button class="remove-condition-btn"
                                                      (click)="removeCondition(i); $event.stopPropagation()"
                                                      title="Remove Condition">
                                                      <i class="fa fa-times"></i>
                                                    </button>
                                                  </div>
                                                  <div class="condition-preview">
                                                    {{ condition.PropertyName || '?' }} {{ condition.Operator }} {{ condition.Value || '?' }}
                                                  </div>
                                                </div>
                                              }
                                            </div>
                                          }
                                          @if ((selectedTransition.Conditions?.length || 0) > 1) {
                                            <div class="conditions-logic">
                                              <i class="fa fa-link"></i>
                                              <span>All conditions must be met (AND logic)</span>
                                            </div>
                                          }
                                        </div>
                                      }
                                      @if (!readonly) {
                                        <div class="panel-actions">
                                          <button class="delete-btn" (click)="removeSelectedTransition()">
                                            <i class="fa fa-trash"></i> Delete Transition
                                          </button>
                                        </div>
                                      }
                                    </div>
                                  }
                                </div>
                              </div>
                            </div>
                          }

                          @if (!hasWorkflow) {
                            <div class="workflow-editor-empty">
                              <div class="empty-state">
                                <i class="fa fa-project-diagram"></i>
                                <p>No workflow data available</p>
                              </div>
                            </div>
                          }
